/**
 * RELATIONSHIP API — Wrapper dei REST Liferay per l'entita' polimorfica.
 *
 * Contratto backend (Liferay RelationshipsResourceImpl):
 * - GET    /relationships/{groupId}?className=<fqn>&classPK=<id>  -> RelationshipLookup { direct, inverse }
 * - GET    /relationships/{groupId}/by-classes?className=<fqn>&otherClassNames=<fqn>,<fqn> -> { items }
 * - POST   /relationships/{groupId}  (body: Relationship)         -> Relationship (409 su duplicato)
 * - DELETE /relationship/{id}  (singolare)                        -> 200
 *
 * NOTA: I campi className* contengono il FQN Java della classe target
 * (es. "it.dvel.crm.core.model.Person"). La conversione short<->FQN
 * avviene nei consumatori (thunks / componenti) via entityRegistry.
 */

import type { Page } from '@crm/shared';
import { httpClient } from '../../api/httpClient';
import type {
  Relationship,
  RelationshipLookup,
  RelationshipsFromContributors,
  ContributorRelationship,
  CreateRelationshipPayload,
  CreateBidirectionalPayload,
  DeleteBidirectionalPayload,
} from './relationshipTypes';

/**
 * Sentinella propagata dalle operazioni bidirezionali quando il forward
 * fallisce con 409 (relazione già esistente). Il caller (thunk o binder)
 * può intercettarla per mostrare un toast dedicato invece dell'errore
 * generico.
 */
export const ERROR_DUPLICATE_RELATIONSHIP = 'DUPLICATE_RELATIONSHIP';

function isConflict(err: unknown): boolean {
  const response = (err as { response?: { status?: number } })?.response;
  return response?.status === 409;
}

export const relationshipApi = {
  /**
   * GET by-classes: tutti i legami del sito fra `className` e le `otherClassNames`, nei due versi
   * (FILO-41). Gli elenchi leggono cosi' i legami di tutti i loro record in una chiamata, non una per
   * record; `lookup` resta per il singolo record.
   */
  byClasses: (groupId: number, className: string, otherClassNames: string[]) =>
    httpClient
      .get<{ items: Relationship[] }>(`/relationships/${groupId}/by-classes`, {
        params: { className, otherClassNames: otherClassNames.join(',') },
      })
      .then((r) => r.data.items),

  /** GET lookup: ritorna {direct, inverse} per l'entita' (className FQN, classPK). */
  lookup: (groupId: number, className: string, classPK: number) =>
    httpClient
      .get<RelationshipLookup>(`/relationships/${groupId}`, {
        params: { className, classPK },
      })
      .then((r) => r.data),

  /**
   * GET from-contributors: per l'entita' (className FQN, classPK) ritorna le
   * relazioni raggruppate per tipo correlato (contributor), gia' risolte in
   * dati leggibili (title/description) e con classPK dell'entita' target +
   * relationshipId per le mutazioni. Un'unica chiamata in luogo del
   * lookup+ricostruzione bidirezionale.
   */
  getRelationshipsFromContributors: (groupId: number, className: string, classPK: number) =>
    httpClient
      .get<RelationshipsFromContributors>(`/relationships/${groupId}/from-contributors`, {
        params: { className, classPK },
      })
      .then((r) => r.data),

  /**
   * GET preview-from-contributors: dato il tipo di entita' che si sta CREANDO
   * (className FQN) e l'entita' di contesto da cui si e' aperta la creazione
   * (referenceClassName FQN + referenceClassPK), ritorna per ogni contributor le
   * entita' da pre-associare a video. Stesso shape di from-contributors, ma le
   * entry hanno relationshipId 0 (la relazione non esiste ancora). Usato in
   * create-mode per seminare il widget Associazioni con le pre-associazioni.
   */
  getPreviewRelationshipsFromContributors: (
    groupId: number,
    className: string,
    referenceClassName: string,
    referenceClassPK: number,
  ) =>
    httpClient
      .get<RelationshipsFromContributors>(
        `/relationships/${groupId}/preview-from-contributors`,
        { params: { className, referenceClassName, referenceClassPK } },
      )
      .then((r) => r.data),

  /**
   * GET contributor-records: cerca le entita' candidate associabili di un
   * singolo tipo (className FQN), delegando al RelationshipContributor lato
   * server. Ritorna un Page di ContributorRelationship (relationshipId 0:
   * l'entita' non e' ancora collegata). Usato dal picker del widget
   * Associazioni al posto del fan-out per-tipo su <entity>Api.getAll.
   */
  getContributorRecords: (
    groupId: number,
    className: string,
    search: string,
    page: number,
    pageSize: number
  ): Promise<Page<ContributorRelationship>> =>
    httpClient
      .get<Page<ContributorRelationship>>(`/relationships/${groupId}/contributor-records`, {
        params: { className, search: search || undefined, page, pageSize },
      })
      .then((r) => r.data),

  /**
   * Fetch bidirezionale: chiama lookup, normalizza gli inverse (swap lati)
   * cosi' appaiono come "source -> target", e deduplica contro i direct.
   * Ritorna un array piatto, stesso shape consumato dallo slice.
   */
  getByEntityBidirectional: async (
    groupId: number,
    className: string,
    classPK: number
  ): Promise<Relationship[]> => {
    const { direct, inverse } = await relationshipApi.lookup(groupId, className, classPK);

    // Indice delle rel "inverse" (entità interrogata = lato 2) per chiave
    // (className1:classPK1) — l'entità del partner. Permette di accoppiare
    // il record inverse al direct dello stesso partner per popolare
    // reverseDescription.
    const inverseByPartner = new Map<string, Relationship>();
    for (const r of inverse) {
      inverseByPartner.set(`${r.className1}:${r.classPK1}`, r);
    }

    // Per ogni direct: arricchisco con reverseDescription presa dalla
    // inverse del partner (= "ruolo del partner verso di me").
    const enrichedDirect = direct.map((r) => {
      const partnerKey = `${r.className2}:${r.classPK2}`;
      const matchInverse = inverseByPartner.get(partnerKey);
      return {
        ...r,
        reverseDescription: matchInverse?.description ?? null,
        reverseRelationshipId: matchInverse?.relationshipId ?? null,
      };
    });

    const directPairs = new Set(
      direct.map((r) => `${r.className2}:${r.classPK2}`)
    );

    // Le inverse che NON hanno un direct corrispondente vengono normalizzate
    // (scambio 1↔2) e mostrate come direct "reverse-only". In quel caso
    // reverseDescription resta null perché non c'è altro lato. Il flag
    // `primary` arriva dal POV inverse (= "l'altro mi considera principale")
    // che non riflette il POV della UI corrente — lo forziamo a false per
    // evitare ambiguità.
    const normalizedInverse = inverse
      .filter((r) => !directPairs.has(`${r.className1}:${r.classPK1}`))
      .map((r) => ({
        ...r,
        className1: r.className2,
        classPK1: r.classPK2,
        className2: r.className1,
        classPK2: r.classPK1,
        // description resta quella del record inverse
        reverseDescription: null,
        reverseRelationshipId: null,
        reverseOnly: true,
        primary: false,
        // pinned segue la stessa logica di `primary`: il POV inverse non
        // riflette quello della UI corrente, quindi lo forziamo a false.
        pinned: false,
      }));

    return [...enrichedDirect, ...normalizedInverse];
  },

  /**
   * Trova il record inverso di una relazione (per la delete bidirezionale).
   * Usa il lookup dal punto di vista dell'entita' 2 e cerca nei direct.
   */
  findReverse: async (
    groupId: number,
    className1: string, classPK1: number,
    className2: string, classPK2: number
  ): Promise<Relationship | null> => {
    const { direct } = await relationshipApi.lookup(groupId, className2, classPK2);
    return direct.find(
      (r) => r.className2 === className1 && r.classPK2 === classPK1
    ) ?? null;
  },

  create: (groupId: number, payload: CreateRelationshipPayload) =>
    httpClient
      .post<Relationship>(`/relationships/${groupId}`, payload)
      .then((r) => r.data),

  /**
   * PATCH del solo flag `primary`. Server-side: se `primary=true` applica
   * demote-others sulla coppia (groupId, className1, classPK1, className2);
   * se `primary=false` toglie il flag senza promuovere altri record.
   */
  setPrimary: (id: number, primary: boolean) =>
    httpClient
      .patch<Relationship>(`/relationship/${id}`, { primary })
      .then((r) => r.data),

  /**
   * PATCH della sola `description` di UN record. Le due descrizioni di una
   * coppia sono due chiamate: le compone il thunk `updateRelationshipDescriptions`.
   */
  updateDescription: (id: number, description: string) =>
    httpClient
      .patch<Relationship>(`/relationship/${id}`, { description })
      .then((r) => r.data),

  /**
   * POST azione: pinna la relazione. Server-side azzera il pin degli altri
   * record della stessa sorgente (al più 1 pinnata per groupId, className1,
   * classPK1). Ritorna il record aggiornato.
   */
  pin: (id: number) =>
    httpClient
      .post<Relationship>(`/relationship/${id}/pin`)
      .then((r) => r.data),

  /** POST azione: toglie il pin dalla relazione (nessun effetto sugli altri). */
  unpin: (id: number) =>
    httpClient
      .post<Relationship>(`/relationship/${id}/unpin`)
      .then((r) => r.data),

  /**
   * GET: true se la sorgente (lato 1) della relazione ha almeno una relazione
   * pinnata. Derivabile anche client-side dal lookup; esposto per simmetria col
   * backend.
   */
  hasPinned: (id: number) =>
    httpClient
      .get<boolean>(`/relationship/${id}/has-pinned`)
      .then((r) => r.data),

  /** DELETE singolare: /relationship/{id} */
  remove: (id: number) =>
    httpClient.delete(`/relationship/${id}`),

  /**
   * Crea relazione bidirezionale: due POST atomici (A->B descForward,
   * B->A descReverse). In caso di 409 sul forward lancia un Error con
   * message `ERROR_DUPLICATE_RELATIONSHIP` (sentinella). Se il secondo
   * POST fallisce, esegue rollback best-effort del primo per evitare
   * relazioni orfane.
   *
   * Usato sia dal thunk `createBidirectionalRelationship` (per i flussi
   * che vogliono aggiornare lo slice globale: FormModal create + drawer
   * standalone) sia direttamente dal binder edit-mode (per gli embed in
   * timeline che gestiscono local state isolato).
   */
  createBidirectional: async (
    groupId: number,
    payload: CreateBidirectionalPayload,
  ): Promise<[Relationship, Relationship]> => {
    let forward: Relationship;
    try {
      forward = await relationshipApi.create(groupId, {
        className1: payload.className1,
        classPK1: payload.classPK1,
        className2: payload.className2,
        classPK2: payload.classPK2,
        description: payload.descriptionForward ?? undefined,
      });
    } catch (err: unknown) {
      if (isConflict(err)) throw new Error(ERROR_DUPLICATE_RELATIONSHIP);
      throw err;
    }
    try {
      const reverse = await relationshipApi.create(groupId, {
        className1: payload.className2,
        classPK1: payload.classPK2,
        className2: payload.className1,
        classPK2: payload.classPK1,
        description: payload.descriptionReverse ?? undefined,
      });
      return [forward, reverse];
    } catch (err: unknown) {
      try {
        await relationshipApi.remove(forward.relationshipId);
      } catch (rollbackErr) {
        console.warn(
          '[relationship] rollback del forward fallito, possibile relazione orfana',
          { relationshipId: forward.relationshipId, rollbackErr }
        );
      }
      if (isConflict(err)) throw new Error(ERROR_DUPLICATE_RELATIONSHIP);
      throw err;
    }
  },

  /**
   * Cancella relazione bidirezionale: DELETE forward + lookup reverse +
   * DELETE reverse. Ritorna gli id eliminati (1 o 2 a seconda che il
   * reverse esista). Se il reverse manca emette un warn per audit ma non
   * rilancia: scenario di dati già inconsistenti.
   */
  deleteBidirectional: async (
    groupId: number,
    payload: DeleteBidirectionalPayload,
  ): Promise<number[]> => {
    await relationshipApi.remove(payload.relationshipId);
    const deletedIds = [payload.relationshipId];
    const reverse = await relationshipApi.findReverse(
      groupId,
      payload.className1, payload.classPK1,
      payload.className2, payload.classPK2
    );
    if (reverse) {
      await relationshipApi.remove(reverse.relationshipId);
      deletedIds.push(reverse.relationshipId);
    } else {
      console.warn(
        '[relationship] reverse mancante sulla delete bidirezionale',
        {
          forwardId: payload.relationshipId,
          from: `${payload.className1}:${payload.classPK1}`,
          to: `${payload.className2}:${payload.classPK2}`,
        }
      );
    }
    return deletedIds;
  },
};
